<template>
  <div v-if="visible" class="add-account-popover">
    <!-- Backdrop -->
    <div class="popover-backdrop" @click="handleClose"></div>
    
    <!-- Popover content -->
    <div class="popover-content">
      <!-- Arrow pointing to button -->
      <div class="popover-arrow"></div>
      
      <!-- Close button -->
      <button class="close-btn" @click="handleClose">
        <i class="el-icon-close"></i>
      </button>
      
      <!-- Header -->
      <div class="popover-header">
        <h3 class="popover-title">{{ modalTitle }}</h3>
        <p class="popover-subtitle">{{ modalSubtitle }}</p>
      </div>

      <!-- Existing accounts -->
      <div v-if="existingAccounts.length > 0" class="existing-accounts-section">
        <div class="section-label">{{ $t("global.account_switcher.your_accounts") }}</div>
        <div class="existing-accounts-list">
          <div 
            v-for="account in existingAccounts" 
            :key="account.email"
            class="account-chip"
          >
            <div class="chip-avatar" :style="{ backgroundColor: account.color }">
              {{ account.initials }}
            </div>
            <span class="chip-name">{{ account.companyName }}</span>
          </div>
        </div>
      </div>

      <!-- Step 1: Email Input -->
      <div v-if="step === 'email'" class="form-section">
        <div class="input-group">
          <label class="input-label">{{ $t("global.account_switcher.email_address") }}</label>
          <el-input
            ref="emailInput"
            v-model="email"
            placeholder="name+company@happily.ai"
            size="small"
            :disabled="loading"
            @keyup.enter.native="submitEmail"
          >
            <i slot="prefix" class="el-icon-message"></i>
          </el-input>
        </div>
        
        <div v-if="error" class="error-message">
          <i class="el-icon-warning"></i>
          {{ error }}
        </div>

        <el-button
          type="primary"
          class="submit-btn"
          size="small"
          :loading="loading"
          :disabled="!email || loading"
          @click="submitEmail"
        >
          {{ loading ? $t("global.account_switcher.sending") : $t("global.account_switcher.continue") }}
        </el-button>
      </div>

      <!-- Step 2: PIN Code -->
      <div v-if="step === 'pincode'" class="form-section">
        <div class="pin-sent-message">
          <i class="el-icon-success"></i>
          <span>{{ $t("global.account_switcher.code_sent_to") }} <strong>{{ truncatedEmail }}</strong></span>
        </div>

        <div class="input-group">
          <label class="input-label">{{ $t("global.account_switcher.verification_code") }}</label>
          <el-input
            ref="pinInput"
            v-model="pincode"
            :placeholder="$t('global.account_switcher.four_digit_code')"
            size="small"
            maxlength="4"
            :disabled="loading"
            @keyup.enter.native="submitPincode"
          >
            <i slot="prefix" class="el-icon-lock"></i>
          </el-input>
        </div>
        
        <div v-if="error" class="error-message">
          <i class="el-icon-warning"></i>
          {{ error }}
        </div>

        <el-button
          type="primary"
          class="submit-btn"
          size="small"
          :loading="loading"
          :disabled="!pincode || pincode.length < 4 || loading"
          @click="submitPincode"
        >
          {{ loading ? $t("global.account_switcher.verifying") : pincodeSubmitLabel }}
        </el-button>

        <button class="back-link" @click="goBackToEmail">
          <i class="el-icon-arrow-left"></i> {{ $t("global.account_switcher.different_email") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import { buildSessionUser, getByUser, getCompany, getLoginData, setSessionUserData } from '@/store/core/cacheService';
import { isAllowedConsoleUserType, requestLoginPin, verifyLoginPin } from '@/store/core/loginPin';

export default {
  name: 'AddAccountModal',
  
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialEmail: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'add',
    },
  },
  
  data() {
    return {
      step: 'email', // 'email' or 'pincode'
      email: '',
      pincode: '',
      loading: false,
      error: '',
      userData: null,      // Cached user data from email verification
      companyData: null,   // Cached company data
      isAdviserUser: false, // Whether user is from adviser table
    };
  },
  
  computed: {
    existingAccounts() {
      return this.$store.getters['accountSwitcher/sortedAccounts'] || [];
    },

    isSwitchMode() {
      return this.mode === 'switch';
    },

    modalTitle() {
      return this.isSwitchMode ? 'Verify advisor account' : this.$t("global.account_switcher.add_account");
    },

    modalSubtitle() {
      return this.isSwitchMode ? 'Enter the advisor email once to enable quick switching.' : this.$t("global.account_switcher.sign_in_plus_email");
    },

    pincodeSubmitLabel() {
      return this.isSwitchMode ? 'Verify and switch' : this.$t("global.account_switcher.add_account");
    },
    
    truncatedEmail() {
      if (!this.email) return '';
      if (this.email.length <= 25) return this.email;
      return this.email.substring(0, 22) + '...';
    },
  },
  
  watch: {
    visible(val) {
      if (val) {
        this.reset();
        this.$nextTick(() => {
          if (this.$refs.emailInput) {
            this.$refs.emailInput.focus();
          }
        });
      }
    },
    initialEmail(val) {
      if (this.visible && val) {
        this.email = String(val || '').toLowerCase();
      }
    },
  },
  
  methods: {
    reset() {
      this.step = 'email';
      this.email = String(this.initialEmail || '').toLowerCase();
      this.pincode = '';
      this.error = '';
      this.loading = false;
      this.userData = null;
      this.companyData = null;
      this.isAdviserUser = false;
    },
    
    handleClose() {
      this.reset();
      this.$emit('close');
    },
    
    goBackToEmail() {
      this.step = 'email';
      this.pincode = '';
      this.error = '';
      this.$nextTick(() => {
        if (this.$refs.emailInput) {
          this.$refs.emailInput.focus();
        }
      });
    },
    
    validateEmail(email) {
      const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
    
    isPlusEmail(email) {
      if (!email) return false;
      const localPart = email.split('@')[0];
      return localPart.includes('+');
    },
    
    async submitEmail() {
      this.error = '';
      
      if (!this.email) {
        this.error = 'Please enter your email address';
        return;
      }
      
      if (!this.validateEmail(this.email)) {
        this.error = 'Please enter a valid email address';
        return;
      }
      
      if (!this.isPlusEmail(this.email)) {
        this.error = 'Please use a + email address (e.g., name+company@domain.com)';
        return;
      }
      
      // Check if this email is already in accounts
      const existingAccount = this.existingAccounts.find(
        a => a.email.toLowerCase() === this.email.toLowerCase()
      );
      if (existingAccount && !this.isSwitchMode) {
        this.error = 'This account is already added. You can switch to it from the sidebar.';
        return;
      }
      
      this.loading = true;
      
      try {
        const emailLower = this.email.toLowerCase();
        const emailKey = emailLower.replace(/\./g, '||');
        
        // Use cache service (same as standard login flow) with fallback to Firebase
        let user = null;
        let company = null;
        let isAdviser = false;  // Track if user is from adviser table
        
        try {
          const loginData = await getLoginData(emailLower);
          user = loginData && loginData.user ? loginData.user : null;
          company = loginData && loginData.company ? loginData.company : null;
          isAdviser = !!(loginData && (loginData.is_adviser || (loginData.user && loginData.user.is_adviser)));
        } catch (loginDataErr) {
          // Fallback to authenticated/cache paths below.
        }

        if (!user) {
          try {
            // Primary: Use cache service when current session is allowed to see this account.
            user = await getByUser(emailLower);
          } catch (cacheErr) {
            // Fallback to Firebase silently
          }
        }
        
        // Fallback: Direct Firebase read if cache service fails
        if (!user) {
          const userSnapshot = await firebase.database().ref(`/users/${emailKey}`).once('value');
          user = userSnapshot.val();
        }
        
        if (!user) {
          this.error = 'User not found. Please check your email.';
          this.loading = false;
          return;
        }
        
        // Get company data using cache service (same as authen.ts)
        if (!company) {
          try {
            company = await getCompany(user.company);
          } catch (companyErr) {
            // Fallback to Firebase silently
            const companySnapshot = await firebase.database().ref(`/company/${user.company}`).once('value');
            company = companySnapshot.val();
          }
        }
        
        // Check if SSO is required (same as auth.ts)
        let ssoEnabled = false;
        try {
          const selectedSso = await this.$store.dispatch('integrations/getSelectedSsoByEmail', emailLower);
          ssoEnabled = selectedSso && selectedSso.hasOwnProperty('enabled') && selectedSso.enabled;
        } catch (ssoErr) {
          // SSO check failed, assume not SSO
        }
        
        if (ssoEnabled && !isAdviser) {
          this.error = 'This account uses SSO. Please log out and sign in with SSO.';
          this.loading = false;
          return;
        }
        
        // Check user type (same as authen.ts login)
        if (!isAllowedConsoleUserType(user.user_type, isAdviser)) {
          this.error = 'Only admin, manager, or leadership accounts can be added.';
          this.loading = false;
          return;
        }
        
        // Store user data for later (same pattern as authen.ts setUserBeforeLogin)
        this.userData = user;
        this.companyData = company;
        this.isAdviserUser = isAdviser;  // Store for later reference
        
        const pinRequest = await requestLoginPin(emailLower, { isAdviser });
        if (!pinRequest.ok) {
          this.error = pinRequest.message || 'Failed to send verification code';
          this.loading = false;
          return;
        }
        
        // Move to PIN step
        this.step = 'pincode';
        this.loading = false;
        this.$nextTick(() => {
          if (this.$refs.pinInput) {
            this.$refs.pinInput.focus();
          }
        });
      } catch (err) {
        console.error('Submit email error:', err);
        this.error = err.message || 'Failed to send verification code';
        this.loading = false;
      }
    },
    
    async submitPincode() {
      this.error = '';
      
      if (!this.pincode || this.pincode.length < 4) {
        this.error = 'Please enter the 4-digit code';
        return;
      }
      
      this.loading = true;
      
      try {
        const emailLower = this.email.toLowerCase();
        const pinVerification = await verifyLoginPin(emailLower, this.pincode, {
          isAdviser: this.isAdviserUser,
        });

        if (!pinVerification.verified) {
          this.error = pinVerification.message || 'Invalid verification code. Please try again.';
          this.loading = false;
          return;
        }

        const emailKey = emailLower.replace(/\./g, '||');

        if (!pinVerification.customToken) {
          this.error = 'Verification is temporarily unavailable. Please try again in a few minutes.';
          this.loading = false;
          return;
        }

        await firebase.auth().signInWithCustomToken(pinVerification.customToken);
        
        // Use cached user/company data or fetch fresh using cache service (same as loginpincode)
        let user = this.userData;
        let company = this.companyData;
        
        if (!user) {
          // Use cache service first (same as authen.ts loginpincode)
          try {
            user = await getByUser(emailLower);
          } catch (e) {
            // Fallback to Firebase
            const userSnapshot = await firebase.database().ref(`/users/${emailKey}`).once('value');
            user = userSnapshot.val();
          }
          
          if (!user) {
            const adviserSnapshot = await firebase.database().ref(`/users_adviser/${emailKey}`).once('value');
            user = adviserSnapshot.val();
          }
        }
        
        if (!user) {
          this.error = 'User not found. Please contact support.';
          this.loading = false;
          return;
        }
        
        if (!company) {
          // Use cache service (same as authen.ts loginpincode)
          try {
            company = await getCompany(user.company);
          } catch (e) {
            const companySnapshot = await firebase.database().ref(`/company/${user.company}`).once('value');
            company = companySnapshot.val();
          }
        }
        
        const userDataObj = buildSessionUser(user, company, this.isAdviserUser || !!pinVerification.is_adviser);
        
        // Update localStorage
        if (!setSessionUserData(userDataObj)) {
          this.error = 'Browser storage is full. Clear site data for this page and try again.';
          this.loading = false;
          return;
        }
        
        // Register account in switcher
        await this.$store.dispatch('accountSwitcher/registerAccount', {
          email: user.email,
          userData: userDataObj,
          isSSO: false,
        });
        
        // Update the authen store
        this.$store.commit('authen/setUserLogin', userDataObj);
        
        // Close modal and reload to switch to new account
        this.$emit('close');
        this.$emit('account-added', userDataObj);
        
        // Reload page to pick up new company data
        window.location.reload();
        
      } catch (err) {
        console.error('Add account error:', err);
        this.error = err.message || 'Failed to add account. Please try again.';
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.add-account-popover {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
}

.popover-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(1px);
}

.popover-content {
  position: absolute;
  left: 60px;
  bottom: 20px;
  width: 300px;
  background: #fff;
  border-radius: 12px;
  box-shadow:
    0 12px 32px rgba(30, 10, 60, 0.16),
    0 2px 8px rgba(30, 10, 60, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 20px;
  animation: popoverSlideIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes popoverSlideIn {
  from {
    opacity: 0;
    transform: translateX(-12px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

.popover-arrow {
  position: absolute;
  left: -7px;
  bottom: 14px;
  width: 14px;
  height: 14px;
  background: #fff;
  transform: rotate(45deg);
  border-left: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #95989A;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 140ms ease, color 140ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #1A1A2E;
  }

  i {
    font-size: 14px;
  }
}

.popover-header {
  margin-bottom: 16px;
  padding-right: 32px;
}

.popover-title {
  font-size: 16px;
  font-weight: 800;
  color: #1A1A2E;
  letter-spacing: -0.1px;
  margin: 0 0 4px;
}

.popover-subtitle {
  font-size: 13px;
  color: #95989A;
  margin: 0;
}

.existing-accounts-section {
  margin-bottom: 16px;
  padding: 12px;
  background: #F5F5F5;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.section-label {
  font-size: 10px;
  font-weight: 700;
  color: #95989A;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  margin-bottom: 10px;
}

.existing-accounts-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.account-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px 6px 6px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.chip-avatar {
  width: 22px;
  height: 22px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  color: #fff;
}

.chip-name {
  font-size: 12px;
  font-weight: 600;
  color: #1A1A2E;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-label {
  font-size: 12px;
  font-weight: 600;
  color: #1A1A2E;
  letter-spacing: 0.1px;
}

.pin-sent-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(58, 196, 125, 0.08);
  border: 1px solid rgba(58, 196, 125, 0.25);
  border-radius: 10px;
  font-size: 12px;
  color: #0F8B55;
  line-height: 1.5;

  i {
    color: #3AC47D;
    font-size: 16px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  strong {
    font-weight: 700;
    color: #1A1A2E;
  }
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(204, 48, 48, 0.08);
  border: 1px solid rgba(204, 48, 48, 0.25);
  border-radius: 10px;
  font-size: 12px;
  color: #CC3030;
  line-height: 1.4;

  i {
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 1px;
  }
}

.submit-btn {
  width: 100%;
  height: 40px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 10px;
  background: linear-gradient(135deg, #6C30CC 0%, #5423A2 100%);
  border: none;
  box-shadow: 0 4px 14px rgba(108, 48, 204, 0.28);
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5423A2 0%, #4A1E8F 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(108, 48, 204, 0.35);
  }

  &:disabled {
    opacity: 0.5;
    box-shadow: none;
  }
}

.back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: none;
  color: #95989A;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px;
  margin-top: 2px;
  transition: color 140ms ease;

  &:hover {
    color: #6C30CC;
  }

  i {
    font-size: 11px;
  }
}
</style>

<style lang="scss">
.add-account-popover {
  .el-input--small {
    .el-input__inner {
      height: 38px;
      border-radius: 10px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      font-size: 13px;
      color: #1A1A2E;
      padding-left: 38px;
      transition: border-color 140ms ease, box-shadow 140ms ease;

      &:focus {
        border-color: #6C30CC;
        box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.12);
      }

      &::placeholder {
        color: #ADADAD;
      }
    }

    .el-input__prefix {
      left: 0;
      width: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #95989A;

      i {
        font-size: 15px;
      }
    }
  }

  .el-button--small {
    padding: 10px 16px;
  }
}
</style>
